import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { studentList, get_classes_by_course_term } from "../../../api/Class";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { handleNavigation } from "../../../../Utils/Utils";
import {
  get_course_term_data,
  add_course_term_data,
} from "../../../api/branches/Branch";

const styles = {
  page: { padding: "20px 24px" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20,
  },
  title: { fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" },
  backBtn: {
    width: 32, height: 32, borderRadius: "50%", border: "1px solid #ddd",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#555", textDecoration: "none", fontSize: 13,
    background: "#fff", transition: "all 0.2s",
  },
  classBar: {
    display: "flex", gap: 24, padding: "10px 16px",
    background: "#f0f7ff", borderRadius: 8, marginBottom: 20,
    fontSize: 13, color: "#333", flexWrap: "wrap",
  },
  classLabel: { color: "#666", marginRight: 4 },
  classValue: { fontWeight: 600 },
  studentSelect: { marginBottom: 20, maxWidth: 480 },
  fieldLabel: {
    fontSize: 11, fontWeight: 600, textTransform: "uppercase",
    color: "#888", marginBottom: 4, letterSpacing: "0.5px",
  },
  divider: { border: "none", borderTop: "1px solid #eee", margin: "0 0 16px 0" },
  termCard: {
    border: "1px solid #e8e8e8", borderRadius: 8,
    marginBottom: 12, overflow: "hidden",
  },
  termHeader: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 14px", background: "#f8f9fa",
    borderBottom: "1px solid #e8e8e8", cursor: "pointer", userSelect: "none",
  },
  termCheckbox: { width: 16, height: 16, cursor: "pointer", accentColor: "#3b82f6", flexShrink: 0 },
  termName: { fontSize: 14, fontWeight: 600, color: "#333", flex: 1 },
  termToggle: { fontSize: 11, color: "#999", marginLeft: "auto" },
  termBody: { padding: "8px 14px 12px" },
  subjectRow: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "8px 0", borderBottom: "1px solid #f0f0f0",
  },
  subjectRowLast: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "8px 0",
  },
  subjectName: {
    width: 180, flexShrink: 0, fontSize: 13, fontWeight: 500,
    color: "#444", paddingTop: 4,
  },
  unitsWrap: {
    display: "flex", flexWrap: "wrap", gap: 6, flex: 1,
  },
  unitChip: (checked) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px", borderRadius: 16, fontSize: 12,
    cursor: "pointer", userSelect: "none", transition: "all 0.15s",
    border: checked ? "1px solid #3b82f6" : "1px solid #ddd",
    background: checked ? "#eff6ff" : "#fff",
    color: checked ? "#1d4ed8" : "#666",
    fontWeight: checked ? 500 : 400,
  }),
  unitCheckbox: { width: 13, height: 13, cursor: "pointer", accentColor: "#3b82f6" },
  saveBar: {
    display: "flex", justifyContent: "flex-end",
    padding: "16px 0", marginTop: 4,
  },
  saveBtn: {
    padding: "10px 32px", background: "#3b82f6", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500,
    cursor: "pointer", transition: "all 0.2s",
  },
  saveBtnDisabled: {
    padding: "10px 32px", background: "#94a3b8", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500,
    cursor: "not-allowed",
  },
};

const AssigneStudent = ({
  type,
  previousData,
  student_id,
  combinePreviousData,
  edi_class_id,
}) => {
  const navigate = useNavigate();
  const branchid = localStorage.getItem("b_id");
  const { grade_id, term_id, class_id, course_id } = useParams();
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm();

  const [studentLists, setStudentList] = useState([]);
  const [selectedSubOptions, setSelectedSubOptions] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [termData, setTermData] = useState({});
  const [termSelectedUnitsByCourse, setTermSelectedUnitsByCourse] = useState({});
  const [expandedTerms, setExpandedTerms] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load student list & class info
  useEffect(() => {
    async function loadData() {
      setStudentList(await studentList(grade_id, term_id, course_id));
      const classResult = await get_classes_by_course_term(branchid, course_id, term_id);
      const classes = classResult?.data || [];
      const current = classes.find((c) => String(c.id) === String(class_id));
      setClassInfo(current || null);
    }
    loadData();
  }, [course_id]);

  // Edit mode: restore previous data
  useEffect(() => {
    if (type === "edit" && previousData) {
      setTermSelectedUnitsByCourse(previousData);
    }
  }, [previousData, type]);

  // Load term data when students are selected
  useEffect(() => {
    if (selectedSubOptions?.length > 0) {
      loadEnrollData();
    } else {
      setTermData({});
      setTermSelectedUnitsByCourse({});
    }
  }, [selectedSubOptions]);

  async function loadEnrollData() {
    if (!course_id) return;
    const result = await get_course_term_data(course_id);
    const courseData = result?.data || [];
    const termsByCourseId = {};
    courseData.forEach((course) => {
      const cId = course.course_id;
      if (!termsByCourseId[cId]) {
        termsByCourseId[cId] = {
          courseName: course.course_name,
          courseTypeName: course.course_type_name,
          course_type_id: course.course_type_id,
          terms: [],
        };
      }
      course.terms.forEach((term) => {
        if (String(term.term_id) === String(term_id)) {
          termsByCourseId[cId].terms.push({
            termName: term.term_name,
            term_id: term.term_id,
            subjects: term.subjects,
          });
        }
      });
    });
    setTermData(termsByCourseId);

    // Auto-select all units
    const preSelected = {};
    Object.keys(termsByCourseId).forEach((cId) => {
      preSelected[cId] = {};
      termsByCourseId[cId].terms.forEach((term, tIdx) => {
        preSelected[cId][tIdx] = {};
        term.subjects.forEach((subj, sIdx) => {
          preSelected[cId][tIdx][sIdx] = subj.units.map((u) => u.unit_id);
        });
      });
    });
    setTermSelectedUnitsByCourse(preSelected);
    setExpandedTerms({});
  }

  const handleStudentChange = (options) => {
    setSelectedSubOptions(options);
  };

  const toggleTerm = (termIndex) => {
    setExpandedTerms((prev) => ({ ...prev, [termIndex]: !prev[termIndex] }));
  };

  const handleLectureInputChange = (e, courseId, termIndex, subjectIndex, unitId) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      if (!updated[courseId]) updated[courseId] = {};
      if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};
      if (!updated[courseId][termIndex][subjectIndex]) {
        updated[courseId][termIndex][subjectIndex] = [];
      }
      const isSelected = updated[courseId][termIndex][subjectIndex].includes(unitId);
      if (isSelected) {
        updated[courseId][termIndex][subjectIndex] = updated[courseId][termIndex][subjectIndex].filter((id) => id !== unitId);
      } else {
        updated[courseId][termIndex][subjectIndex].push(unitId);
      }
      if (updated[courseId][termIndex][subjectIndex].length === 0) delete updated[courseId][termIndex][subjectIndex];
      if (Object.keys(updated[courseId][termIndex] || {}).length === 0) delete updated[courseId][termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  const handleTermCheckboxChange = (courseId, termIndex) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      if (!updated[courseId]) updated[courseId] = {};
      const allSelected = termData[courseId]?.terms[termIndex]?.subjects.every(
        (subject, sIdx) =>
          subject.units.every((unit) =>
            updated[courseId]?.[termIndex]?.[sIdx]?.includes(unit.unit_id)
          )
      );
      if (allSelected) {
        delete updated[courseId][termIndex];
      } else {
        if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};
        termData[courseId].terms[termIndex].subjects.forEach((subject, sIdx) => {
          updated[courseId][termIndex][sIdx] = subject.units.map((u) => u.unit_id);
        });
      }
      if (Object.keys(updated[courseId]?.[termIndex] || {}).length === 0) delete updated[courseId]?.[termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const submissionData = [];
    Object.keys(termData).forEach((courseId) => {
      const course = termData[courseId];
      const courseTerms = course?.terms || [];
      courseTerms.forEach((term, termIndex) => {
        const termUnits = termSelectedUnitsByCourse[courseId]?.[termIndex] || {};
        term.subjects.forEach((subject, subjectIndex) => {
          (subject.units || []).forEach((unit) => {
            const isUnitSelected = termUnits[subjectIndex]?.includes(unit.unit_id) || false;
            submissionData.push({
              course_id: courseId,
              term_name: term.termName,
              term_id: term.term_id,
              subject_id: subject.subject_id,
              unit_id: unit.unit_id,
              course_type_name: course.courseTypeName,
              course_type_id: course.course_type_id,
              course_name: course.courseName,
              status: isUnitSelected ? 1 : 0,
            });
          });
        });
      });
    });

    const combinedData =
      type === "edit"
        ? combinePreviousData(submissionData, previousData)
        : submissionData;

    const payload = {
      student_id:
        type === "edit"
          ? student_id
          : selectedSubOptions?.map((ele) => ele?.value),
      courses: combinedData,
    };

    const result = await add_course_term_data(payload, class_id, branchid);
    if (result?.status === true) {
      navigate("/branch/class-management/all");
    } else {
      console.error("Save Failed", result);
    }
    setIsSubmitting(false);
  };

  const hasTermData = Object.keys(termData).length > 0;

  return (
    <div className="right-nav" id="dash-event" style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h4 style={styles.title}>Assign Students</h4>
        <Link
          to="/branch/class-management/all"
          style={styles.backBtn}
          onClick={(e) => handleNavigation(e, isDirty)}
        >
          <i className="fa fa-arrow-left"></i>
        </Link>
      </div>

      {/* Class Info Bar */}
      {classInfo && (
        <div style={styles.classBar}>
          <span>
            <span style={styles.classLabel}>Class</span>{" "}
            <span style={styles.classValue}>{classInfo.class_name}</span>
          </span>
          {classInfo.instructor_name && (
            <span>
              <span style={styles.classLabel}>Instructor</span>{" "}
              <span style={styles.classValue}>{classInfo.instructor_name}</span>
            </span>
          )}
          {classInfo.class_timing && (
            <span>
              <span style={styles.classLabel}>Time</span>{" "}
              <span style={styles.classValue}>{classInfo.class_timing}</span>
            </span>
          )}
          {classInfo.student_count !== undefined && (
            <span>
              <span style={styles.classLabel}>Students</span>{" "}
              <span style={styles.classValue}>{classInfo.student_count}</span>
            </span>
          )}
        </div>
      )}

      {/* Student Select */}
      {type !== "edit" && (
        <div style={styles.studentSelect}>
          <div style={styles.fieldLabel}>Select students</div>
          <Controller
            name="student"
            control={control}
            defaultValue={[]}
            rules={{ required: "This is required" }}
            render={({ field }) => (
              <Select
                options={studentLists}
                placeholder="Search and select students..."
                isMulti
                {...field}
                value={selectedSubOptions}
                onChange={(options) => {
                  handleStudentChange(options);
                  field.onChange(options);
                }}
                styles={{
                  control: (base) => ({ ...base, fontSize: 13, borderColor: "#ddd", minHeight: 38 }),
                  option: (base) => ({ ...base, fontSize: 13, textTransform: "none" }),
                  multiValue: (base) => ({ ...base, textTransform: "none" }),
                  placeholder: (base) => ({ ...base, fontSize: 13, color: "#aaa" }),
                }}
              />
            )}
          />
          {errors.student && (
            <span style={{ color: "#e74c3c", fontSize: 11, marginTop: 4, display: "block" }}>
              {errors.student.message}
            </span>
          )}
        </div>
      )}

      {/* Term / Subject / Unit Selection */}
      {hasTermData && (
        <>
          <hr style={styles.divider} />
          {Object.keys(termData).map((courseId) =>
            termData[courseId].terms.map((term, termIndex) => {
              const isTermSelected = term.subjects.every(
                (subject, sIdx) =>
                  subject.units.every((unit) =>
                    termSelectedUnitsByCourse[courseId]?.[termIndex]?.[sIdx]?.includes(unit.unit_id)
                  )
              );
              const isExpanded = expandedTerms[termIndex] === true;
              return (
                <div key={termIndex} style={styles.termCard}>
                  <div style={styles.termHeader} onClick={() => toggleTerm(termIndex)}>
                    <input
                      type="checkbox"
                      style={styles.termCheckbox}
                      checked={isTermSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleTermCheckboxChange(courseId, termIndex)}
                    />
                    <span style={styles.termName}>Term {term.termName}</span>
                    <span style={styles.termToggle}>
                      <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                    </span>
                  </div>
                  {isExpanded && (
                    <div style={styles.termBody}>
                      {term.subjects.map((subjectObj, subjectIndex) => (
                        <div
                          key={subjectIndex}
                          style={subjectIndex < term.subjects.length - 1 ? styles.subjectRow : styles.subjectRowLast}
                        >
                          <div style={styles.subjectName}>{subjectObj.subject_name}</div>
                          <div style={styles.unitsWrap}>
                            {subjectObj.units.map((unit, unitIndex) => {
                              const checked =
                                termSelectedUnitsByCourse[courseId]?.[termIndex]?.[subjectIndex]?.includes(unit.unit_id) || false;
                              return (
                                <label key={unitIndex} style={styles.unitChip(checked)}>
                                  <input
                                    type="checkbox"
                                    style={styles.unitCheckbox}
                                    checked={checked}
                                    onChange={(e) =>
                                      handleLectureInputChange(e, courseId, termIndex, subjectIndex, unit.unit_id)
                                    }
                                  />
                                  {unit.unit_name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Save Button */}
          <div style={styles.saveBar}>
            <button
              style={isSubmitting ? styles.saveBtnDisabled : styles.saveBtn}
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AssigneStudent;
